import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FeaturedTutorialVideoCard } from './FeaturedTutorialVideoCard';
import { FeaturedQAItem } from './FeaturedQAItem';
import { FeaturedTriviaItem } from './FeaturedTriviaItem';
import './FeaturedContentGrid.css';

export function FeaturedContentGrid({
  tutorialVideos = [],
  questionsAnswers = [],
  triviaQuestions = [],
  featuredVideos: overrideFeaturedVideos,
  featuredQA: overrideFeaturedQA,
  featuredTrivia: overrideFeaturedTrivia,
  showViewAllButtons = true,
  isAdmin = false,
  onDeleteVideo,
  onDeleteQA,
  onDeleteTrivia,
  onReorderFeaturedItem
}) {
  const navigate = useNavigate();

  // Use override data if provided, otherwise slice from full arrays
  const featuredVideos = overrideFeaturedVideos || (tutorialVideos?.slice ? tutorialVideos.slice(0, 3) : []);
  const featuredQA = overrideFeaturedQA || (questionsAnswers?.slice ? questionsAnswers.slice(0, 3) : []);
  const featuredTrivia = overrideFeaturedTrivia || (triviaQuestions?.slice ? triviaQuestions.slice(0, 3) : []);

  const handleVideoClick = (video) => {
    // Navigate to correct URL format with category tab
    navigate(`/learn/tutorial-videos?video=${video.id}&tab=${video.categoryId}`);
  };

  const handleQAClick = (qaId) => {
    navigate(`/learn/questions-answers?qa=${encodeURIComponent(qaId)}`);
  };

  const handleTriviaClick = () => {
    navigate('/learn/trivia');
  };

  const handleReorderClick = (e, section, id, direction) => {
    e.preventDefault();
    e.stopPropagation();
    onReorderFeaturedItem?.(section, id, direction);
  };

  return (
    <div className="learn-featured-grid">
      {/* Tutorial Videos Column */}
      <div className="learn-featured-column">
        <div className="learn-column-header">
          <h2>Tutorial Videos</h2>
          {showViewAllButtons && (
            <button className="learn-view-all-btn" onClick={() => navigate('/learn/tutorial-videos')}>
              View All <i className="fa-solid fa-arrow-right"></i>
            </button>
          )}
        </div>
        <div className="learn-featured-items">
          {featuredVideos.map((video, index) => (
            <div key={video.id} className="featured-video-admin-wrapper">
              <FeaturedTutorialVideoCard
                video={video}
                onVideoClick={() => handleVideoClick(video)}
              />
              {isAdmin && (onDeleteVideo || onReorderFeaturedItem) && (
                <div className="featured-video-admin-controls">
                  {onReorderFeaturedItem && (
                    <div className="featured-item-reorder-controls" role="group" aria-label="Reorder featured video">
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index === 0}
                        title="Move up"
                        aria-label="Move featured video up"
                        onClick={(e) => handleReorderClick(e, 'tutorial-videos', video.id, -1)}
                      >
                        <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index >= featuredVideos.length - 1}
                        title="Move down"
                        aria-label="Move featured video down"
                        onClick={(e) => handleReorderClick(e, 'tutorial-videos', video.id, 1)}
                      >
                        <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {onDeleteVideo && (
                    <button
                      className="admin-delete-btn"
                      onClick={() => onDeleteVideo(video.id)}
                      title="Remove from featured content"
                      aria-label="Remove video from featured content"
                    >
                      <i className="fa-solid fa-trash" />
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Questions & Answers Column */}
      <div className="learn-featured-column">
        <div className="learn-column-header">
          <h2>Questions & Answers</h2>
          {showViewAllButtons && (
            <button className="learn-view-all-btn" onClick={() => navigate('/learn/questions-answers')}>
              View All <i className="fa-solid fa-arrow-right"></i>
            </button>
          )}
        </div>
        <div className="learn-featured-items">
          {featuredQA.map((qa, index) => (
            <div key={qa.id} className="featured-qa-admin-wrapper">
              <FeaturedQAItem
                qa={qa}
                onQAClick={handleQAClick}
              />
              {isAdmin && (onDeleteQA || onReorderFeaturedItem) && (
                <div className="featured-qa-admin-controls">
                  {onReorderFeaturedItem && (
                    <div className="featured-item-reorder-controls" role="group" aria-label="Reorder featured Q and A">
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index === 0}
                        title="Move up"
                        aria-label="Move featured question up"
                        onClick={(e) => handleReorderClick(e, 'questions-answers', qa.id, -1)}
                      >
                        <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index >= featuredQA.length - 1}
                        title="Move down"
                        aria-label="Move featured question down"
                        onClick={(e) => handleReorderClick(e, 'questions-answers', qa.id, 1)}
                      >
                        <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {onDeleteQA && (
                    <button
                      className="admin-delete-btn"
                      onClick={() => onDeleteQA(qa.id)}
                      title="Remove from featured content"
                      aria-label="Remove Q&A from featured content"
                    >
                      <i className="fa-solid fa-trash" />
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Trivia Column */}
      <div className="learn-featured-column">
        <div className="learn-column-header">
          <h2>Trivia</h2>
          {showViewAllButtons && (
            <button className="learn-view-all-btn" onClick={handleTriviaClick}>
              View All <i className="fa-solid fa-arrow-right"></i>
            </button>
          )}
        </div>
        <div className="learn-featured-items">
          {featuredTrivia.map((trivia, index) => (
            <div key={trivia.id} className="featured-trivia-admin-wrapper">
              <FeaturedTriviaItem
                trivia={trivia}
                onTriviaClick={handleTriviaClick}
              />
              {isAdmin && (onDeleteTrivia || onReorderFeaturedItem) && (
                <div className="featured-trivia-admin-controls">
                  {onReorderFeaturedItem && (
                    <div className="featured-item-reorder-controls" role="group" aria-label="Reorder featured trivia">
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index === 0}
                        title="Move up"
                        aria-label="Move featured trivia up"
                        onClick={(e) => handleReorderClick(e, 'trivia', trivia.id, -1)}
                      >
                        <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="featured-reorder-btn"
                        disabled={index >= featuredTrivia.length - 1}
                        title="Move down"
                        aria-label="Move featured trivia down"
                        onClick={(e) => handleReorderClick(e, 'trivia', trivia.id, 1)}
                      >
                        <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {onDeleteTrivia && (
                    <button
                      className="admin-delete-btn"
                      onClick={() => onDeleteTrivia(trivia.id)}
                      title="Remove from featured content"
                      aria-label="Remove trivia from featured content"
                    >
                      <i className="fa-solid fa-trash" />
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
